import { useEffect, useRef } from "react";
import gsap from "gsap";
import { prefersReducedMotion } from "../../lib/motion";

const CustomCursor = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    const label = labelRef.current;
    const supportsFinePointer = window.matchMedia("(pointer: fine)").matches;
    const reducedMotion = prefersReducedMotion();

    if (!cursor || !label || !supportsFinePointer || reducedMotion) {
      return;
    }

    const moveX = gsap.quickTo(cursor, "x", {
      duration: 0.22,
      ease: "power3.out",
    });
    const moveY = gsap.quickTo(cursor, "y", {
      duration: 0.22,
      ease: "power3.out",
    });

    const handlePointerMove = (event: PointerEvent) => {
      moveX(event.clientX);
      moveY(event.clientY);
      cursor.classList.add("is-visible");

      const target = event.target as HTMLElement;
      const interactive = target.closest<HTMLElement>("[data-cursor]");
      const formControl = target.closest("button, input, textarea, select");

      if (formControl && !interactive) {
        cursor.classList.add("is-hidden");
        return;
      }

      cursor.classList.remove("is-hidden");
      const cursorLabel = interactive?.dataset.cursor ?? "";
      label.textContent = cursorLabel;
      cursor.classList.toggle("has-label", Boolean(cursorLabel));
    };

    const handlePointerLeave = () => cursor.classList.remove("is-visible");

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    document.documentElement.addEventListener(
      "pointerleave",
      handlePointerLeave,
    );

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      document.documentElement.removeEventListener(
        "pointerleave",
        handlePointerLeave,
      );
    };
  }, []);

  return (
    <div ref={cursorRef} className="custom-cursor" aria-hidden="true">
      <span ref={labelRef} />
    </div>
  );
};

export default CustomCursor;
